/**
 * LAMED Main Application Component
 * Learning and Multimodal Exploration Device - Voice Interface with Authentication
 *
 * TypeScript-based React application with voice AI interaction.
 */

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Import context providers
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ModuleRefreshProvider } from './contexts/ModuleRefreshContext';

// Import components
import Login from './components/Login';
import VoiceInterface from './components/VoiceInterface';
import NetworkStatus from './components/NetworkStatus';

// Import module components
import CalendarModule from './components/modules/CalendarModule';
import KnowledgeBaseModule from './components/modules/KnowledgeBaseModule';
import CurriculumModule from './components/modules/CurriculumModule';
import ExamsModule from './components/modules/ExamsModule';
// Tasks and Email modules are disabled - kept as code samples
// import TasksModule from './components/modules/TasksModule';
// import EmailModule from './components/modules/EmailModule';
import AdminPanel from './components/AdminPanel';

/**
 * Protected Route Component
 * Redirects to login if not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Admin Route Component
 * Requires authentication AND admin role
 */
const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has admin role
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">Admin access required</p>
          <button
            onClick={() => window.location.href = '/voice'}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Voice Interface
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * App Content Component - Renders persistent VoiceInterface + Routes
 */
const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Determine if VoiceInterface should be minimized (floating button)
  // Tasks and Email routes are disabled - kept as code samples
  const isModuleRoute = ['/calendar', '/knowledge', '/curriculum', '/exams'].includes(location.pathname);
  const isAdminRoute = location.pathname === '/admin';
  const showFloatingVoice = isAuthenticated && isModuleRoute;

  return (
    <>
      {/* Persistent Voice Interface - Always mounted when authenticated (except admin panel) */}
      {isAuthenticated && location.pathname !== '/login' && !isAdminRoute && (
        <VoiceInterface
          key="persistent-voice"
          minimized={showFloatingVoice}
        />
      )}

      {/* Route Content */}
      <Routes>
        {/* Login Route */}
        <Route path="/login" element={<Login />} />

        {/* Voice Interface Route (Protected) - Now just a placeholder since VoiceInterface is persistent */}
        <Route
          path="/voice"
          element={
            <ProtectedRoute>
              <div /> {/* Empty - VoiceInterface renders above */}
            </ProtectedRoute>
          }
        />

        {/* Module Routes (Protected) */}
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <CalendarModule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge"
          element={
            <ProtectedRoute>
              <KnowledgeBaseModule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/curriculum"
          element={
            <ProtectedRoute>
              <CurriculumModule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exams"
          element={
            <ProtectedRoute>
              <ExamsModule />
            </ProtectedRoute>
          }
        />
        {/* Tasks and Email routes are disabled - kept as code samples */}
        {/* <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TasksModule />
            </ProtectedRoute>
          }
        />
        <Route
          path="/email"
          element={
            <ProtectedRoute>
              <EmailModule />
            </ProtectedRoute>
          }
        /> */}

        {/* Admin Panel Route (Admin Only) */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />

        {/* Default Route - Redirect to Voice Interface */}
        <Route path="/" element={<Navigate to="/voice" replace />} />

        {/* Catch all - Redirect to Voice Interface */}
        <Route path="*" element={<Navigate to="/voice" replace />} />
      </Routes>
    </>
  );
};

/**
 * Main Application Component
 */
function App(): JSX.Element {
  return (
    <Router>
      <AuthProvider>
        <ModuleRefreshProvider>
          {/* Global Network Status Monitor */}
          <NetworkStatus />

          <AppContent />
        </ModuleRefreshProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
